import { usePpsAccess } from 'hooks/usePpsAccess';
import { ReactNode, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ProgressSpinner } from 'primereact/progressspinner';

interface PermissionGuardHocProps {
  requiredPermission: string;
  redirectTo?: string;
  children?: ReactNode;
}

/**
 * A HOC that guards routes based on user permissions
 */
const PermissionGuardHoc = ({
  requiredPermission,
  redirectTo = '/job-paste', // Default redirect
  children
}: PermissionGuardHocProps) => {
  const { hasPermission, isLoading } = usePpsAccess();
  const navigate = useNavigate();

  // Handle multiple permissions if comma-separated
  const permissions = requiredPermission.split(',');

  // Check if user has at least one of the required permissions
  const hasRequiredPermission = () => {
    return permissions.some(permission => hasPermission(permission));
  };

  // Handle unauthorized access
  useEffect(() => {
    if (!isLoading && !hasRequiredPermission()) {
      // Show toast notification
      toast.error('You are not authorized to access this page');

      // Navigate immediately
      navigate(redirectTo, { replace: true });
    }
  }, [isLoading]);

  // While loading, show a loading spinner
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          width: '100vw'
        }}
      >
        <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="5" />
        <p style={{ marginTop: '20px', fontSize: '16px' }}>Loading user permissions...</p>
      </div>
    );
  }

  // If the user doesn't have the required permission, render nothing
  // (navigation will happen in the effect)
  if (!hasRequiredPermission()) {
    return null;
  }

  // If children are provided, render them, otherwise render the outlet
  return children ? <>{children}</> : <Outlet />;
};

export default PermissionGuardHoc;
